// src/lib/AppContext.js
import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabase';
import { COUNTRY_CONFIG, formatDisplayPrice, getShippingEur } from './currency';

const AppContext = createContext(null);

// ── Cart persistence helpers ──────────────────────────────────────────────────
const CART_KEY = 'cq_cart';
function loadCart() {
  try { const r = localStorage.getItem(CART_KEY); return r ? JSON.parse(r) : []; } catch { return []; }
}
function saveCart(items) {
  try { localStorage.setItem(CART_KEY, JSON.stringify(items)); } catch {}
}

const initialState = {
  user: null,
  profile: null,
  authLoading: true,
  profileLoading: false,
  lang: 'en',
  country: 'DE',
  displayCurrency: 'EUR',
  cartItems: loadCart(),
  ageVerified: false,
  cartOpen: false,
  searchQuery: '',
  activeCategory: 'new-arrivals',
  toasts: [],
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_USER':           return { ...state, user: action.payload };
    case 'SET_PROFILE':        return { ...state, profile: action.payload, profileLoading: false };
    case 'SET_AUTH_LOADING':   return { ...state, authLoading: action.payload };
    case 'SET_PROFILE_LOADING':return { ...state, profileLoading: action.payload };
    case 'SET_LANG':           return { ...state, lang: action.payload };
    case 'SET_LOCALE': {
      const config = COUNTRY_CONFIG[action.payload] || COUNTRY_CONFIG.DE;
      return { ...state, country: action.payload, displayCurrency: config.currency };
    }
    case 'SET_AGE_VERIFIED': return { ...state, ageVerified: true };
    case 'CART_ADD': {
      const { product, qty = 1 } = action.payload;
      const existing = state.cartItems.find(i => i.id === product.id);
      if (existing) {
        return { ...state, cartItems: state.cartItems.map(i => i.id === product.id ? { ...i, qty: Math.min(i.qty + qty, i.stock || 99) } : i) };
      }
      return { ...state, cartItems: [...state.cartItems, { ...product, qty: Math.min(qty, product.stock || 99) }] };
    }
    case 'CART_REMOVE':     return { ...state, cartItems: state.cartItems.filter(i => i.id !== action.payload) };
    case 'CART_UPDATE_QTY': return { ...state, cartItems: state.cartItems.map(i => i.id === action.payload.id ? { ...i, qty: Math.max(1, Math.min(action.payload.qty, i.stock || 99)) } : i) };
    case 'CART_CLEAR':      return { ...state, cartItems: [] };
    case 'CART_TOGGLE':     return { ...state, cartOpen: !state.cartOpen };
    case 'CART_CLOSE':      return { ...state, cartOpen: false };
    case 'SET_SEARCH':      return { ...state, searchQuery: action.payload };
    case 'SET_ACTIVE_CATEGORY': return { ...state, activeCategory: action.payload };
    case 'ADD_TOAST':    return { ...state, toasts: [...state.toasts, { id: Date.now(), ...action.payload }] };
    case 'REMOVE_TOAST': return { ...state, toasts: state.toasts.filter(t => t.id !== action.payload) };
    default: return state;
  }
}

// ─── fetchProfile: loads a single user's own profile row ───────────────────
// Only reads the user's own row (auth.uid() = id policy allows this).
// Returns null on any error rather than throwing.
async function fetchProfile(userId) {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, full_name, role, loyalty_points, loyalty_tier, preferred_language, preferred_country, wishlist, spin_balance, last_weekly_spin_at, newsletter_spin_claimed, total_spins_used')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('[CosmiqHerbz] fetchProfile error:', error.message, error.code);
      return null;
    }
    return data;
  } catch (e) {
    console.error('[CosmiqHerbz] fetchProfile exception:', e);
    return null;
  }
}

// ─── ensureProfile: creates the profile row if it doesn't exist ────────────
// This handles the case where the signup trigger didn't fire (e.g. OAuth users)
async function ensureProfile(user) {
  let profile = await fetchProfile(user.id);
  if (!profile) {
    console.log('[CosmiqHerbz] No profile found — creating one for', user.email);
    const { data, error } = await supabase
      .from('profiles')
      .insert({
        id: user.id,
        email: user.email,
        full_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'User',
        role: 'customer',
      })
      .select()
      .single();
    if (error) {
      console.error('[CosmiqHerbz] Could not create profile:', error.message);
    } else {
      profile = data;
    }
  }
  return profile;
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const busyRef = useRef(false);

  // ── 0. Persist cart to localStorage whenever it changes ──────────────────
  useEffect(() => { saveCart(state.cartItems); }, [state.cartItems]);

  // ── 1. Restore session preferences (instant, no network) ────────────────
  useEffect(() => {
    const savedLang    = sessionStorage.getItem('cq_lang');
    const savedCountry = sessionStorage.getItem('cq_country');
    const ageOk        = sessionStorage.getItem('cq_age') === '1';
    if (savedLang)    dispatch({ type: 'SET_LANG',   payload: savedLang });
    if (savedCountry) dispatch({ type: 'SET_LOCALE', payload: savedCountry });
    if (ageOk)        dispatch({ type: 'SET_AGE_VERIFIED' });
  }, []);

  // ── 2. Bootstrap auth ────────────────────────────────────────────────────
  // Pattern: getSession() first so we don't wait for the listener,
  // then listen for changes (logout, token refresh, OAuth callback).
  useEffect(() => {
    let mounted = true;

    const loadUser = async (session) => {
      if (busyRef.current) return;
      busyRef.current = true;
      try {
        if (!session?.user) {
          if (mounted) {
            dispatch({ type: 'SET_USER',    payload: null });
            dispatch({ type: 'SET_PROFILE', payload: null });
          }
          return;
        }
        if (mounted) dispatch({ type: 'SET_USER', payload: session.user });
        const profile = await ensureProfile(session.user);
        if (mounted) {
          dispatch({ type: 'SET_PROFILE', payload: profile });
          if (profile?.preferred_country)  dispatch({ type: 'SET_LOCALE', payload: profile.preferred_country });
          if (profile?.preferred_language) dispatch({ type: 'SET_LANG',   payload: profile.preferred_language });
        }
      } finally {
        busyRef.current = false;
        if (mounted) dispatch({ type: 'SET_AUTH_LOADING', payload: false });
      }
    };

    // Get existing session immediately
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (mounted) loadUser(session);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;
        if (event === 'SIGNED_OUT') {
          dispatch({ type: 'SET_USER',         payload: null });
          dispatch({ type: 'SET_PROFILE',      payload: null });
          dispatch({ type: 'SET_AUTH_LOADING', payload: false });
          busyRef.current = false;
          return;
        }
        if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
          loadUser(session);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // ── 3. Re-fetch profile on tab focus (picks up SQL role changes) ─────────
  useEffect(() => {
    const onFocus = async () => {
      if (busyRef.current) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const profile = await fetchProfile(session.user.id);
      if (profile) dispatch({ type: 'SET_PROFILE', payload: profile });
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // ── Derived values ────────────────────────────────────────────────────────
  const cartSubtotalEur = state.cartItems.reduce((s, i) => s + i.price_eur * i.qty, 0);
  const cartShippingEur = getShippingEur(cartSubtotalEur, state.country);
  const cartTotalEur    = cartSubtotalEur + cartShippingEur;
  const cartCount       = state.cartItems.reduce((s, i) => s + i.qty, 0);
  const isAdmin = state.profile?.role === 'admin';
  const isStaff = ['admin', 'staff'].includes(state.profile?.role);

  const addToCart    = useCallback((product) => dispatch({ type: 'CART_ADD',        payload: { product } }), []);
  const removeFromCart = useCallback((id)    => dispatch({ type: 'CART_REMOVE',     payload: id }),         []);
  const updateQty    = useCallback((id, qty) => dispatch({ type: 'CART_UPDATE_QTY', payload: { id, qty } }), []);
  const clearCart    = useCallback(()         => dispatch({ type: 'CART_CLEAR' }),                           []);

  const setLang = useCallback((lang) => {
    dispatch({ type: 'SET_LANG', payload: lang });
    sessionStorage.setItem('cq_lang', lang);
  }, []);

  const setCountry = useCallback(async (country, userId) => {
    dispatch({ type: 'SET_LOCALE', payload: country });
    sessionStorage.setItem('cq_country', country);
    if (userId) await supabase.from('profiles').update({ preferred_country: country }).eq('id', userId);
  }, []);

  const verifyAge = useCallback((lang, country) => {
    dispatch({ type: 'SET_AGE_VERIFIED' });
    if (lang)    dispatch({ type: 'SET_LANG',   payload: lang });
    if (country) dispatch({ type: 'SET_LOCALE', payload: country });
    sessionStorage.setItem('cq_age',     '1');
    sessionStorage.setItem('cq_lang',    lang    || 'en');
    sessionStorage.setItem('cq_country', country || 'DE');
  }, []);

  const showToast = useCallback((message, type = 'info') => {
    const id = Date.now();
    dispatch({ type: 'ADD_TOAST', payload: { id, type, message } });
    setTimeout(() => dispatch({ type: 'REMOVE_TOAST', payload: id }), 3500);
  }, []);

  // Expose a manual profile refresh (useful for debug page)
  const refreshProfile = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    const profile = await fetchProfile(session.user.id);
    if (profile) dispatch({ type: 'SET_PROFILE', payload: profile });
    return profile;
  }, []);

  const value = {
    ...state,
    cartSubtotalEur, cartShippingEur, cartTotalEur, cartCount,
    isAdmin, isStaff,
    dispatch,
    addToCart, removeFromCart, updateQty, clearCart,
    setLang, setCountry, verifyAge, showToast, refreshProfile,
    fmt:    (eur) => formatDisplayPrice(eur, state.displayCurrency),
    fmtEur: (eur) => `€${eur.toFixed(2)}`,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}